import axios from "axios";

// ── Helpers ─────────────────────────────────────────────────────────────────

const resolveFileType = (file) => {
  const extFromName = file?.name?.includes(".")
    ? "." + file.name.split(".").pop().toLowerCase()
    : "";
  const mimeToExt = (m) =>
    m === "image/png"                               ? ".png"
    : m === "image/jpeg" || m === "image/jpg"       ? ".jpg"
    : m === "video/mp4"                             ? ".mp4"
    : m === "audio/mpeg"                            ? ".mp3"
    : m === "audio/wav" || m === "audio/x-wav"      ? ".wav"
    : m === "audio/ogg"                             ? ".ogg"
    : m === "audio/webm" || m === "video/webm"      ? ".webm"
    : "";
  return extFromName || mimeToExt(file?.type || "");
};

const writeSession = (data) => {
  try { localStorage.setItem("quantumUploadSession", JSON.stringify(data)); } catch {}
};

const updateSession = (patch) => {
  try {
    const existing = JSON.parse(localStorage.getItem("quantumUploadSession") || "{}");
    localStorage.setItem("quantumUploadSession", JSON.stringify({ ...existing, ...patch }));
  } catch {}
};

// ── Main export ──────────────────────────────────────────────────────────────
//
// Industry-standard pattern for background uploads in an SPA:
//   • Module-level async function — lives outside React, survives navigation.
//   • Progress is written to localStorage so GlobalAIReportProgressBanner can
//     read it from any page via its 500 ms polling interval.
//   • checkedOnce:true from the very first write so the banner shows immediately.
//
// Simulation strategy (solves the "stuck" problem):
//   • Time-based simulation instead of tick-counting.  Even if the JS event
//     loop is momentarily blocked by heavy page renders (SmartReport,
//     SymptomChecker…), the NEXT tick advances progress to wherever it should
//     be by now — no frozen stretches.
//   • Two-speed simulation: fast warm-up → slow drip.  After the warm-up
//     phase reaches its target the drip continues at 1 % / 3 s so the bar
//     never hard-stops while waiting for a slow API response.
//   • stopSimulation() is called before every real milestone jump so the
//     simulated and real values never fight each other.

export const startGlobalUpload = async ({
  file,
  serviceName,
   sourcechannel,  
  createdBy,
  patientId,
  endpointBase,
  onProgress,
  onSuccess,
  onError,
}) => {
  let timer   = null;
  let current = 0;           // always-increasing integer 0-100

  // ── Helpers ────────────────────────────────────────────────────────────────

  const advance = (n) => {
    current = Math.min(100, Math.max(current, Math.round(n)));
    updateSession({ progress: current });
    onProgress?.(current);
  };

  const stopTimer = () => { clearInterval(timer); timer = null; };

  /**
   * Time-based simulation.
   *
   * Phases:
   *   1. Fast warm-up: current → fastEnd over fastMs (looks responsive).
   *   2. Slow drip   : fastEnd → cap  at 1 % per slowMs (never freezes while
   *                    waiting for a slow API response).
   *
   * The interval fires frequently (every 100 ms) but only advances progress
   * if enough real-world time has elapsed.  This survives JS-thread blocking
   * from heavy page renders because the NEXT unblocked tick compensates.
   */
  const simulate = (fastEnd, fastMs, cap, slowMs = 3000) => {
    stopTimer();
    const origin    = current;
    const startedAt = Date.now();

    timer = setInterval(() => {
      const elapsed = Date.now() - startedAt;

      if (elapsed <= fastMs) {
        // Fast phase — map elapsed time linearly to (origin → fastEnd)
        const target = origin + Math.round(((fastEnd - origin) * elapsed) / fastMs);
        advance(Math.min(target, fastEnd));
      } else {
        // Slow drip phase — 1 % per slowMs after the fast phase finished
        const drip = fastEnd + Math.floor((elapsed - fastMs) / slowMs);
        advance(Math.min(drip, cap));
        if (current >= cap) stopTimer();
      }
    }, 100); // poll often so each tick makes a tiny move (never feels frozen)
  };

  // ── Upload flow ─────────────────────────────────────────────────────────────

  try {
    const resolvedFileType = resolveFileType(file);
    const ct               = file?.type || "application/octet-stream";

    // ── Init: banner visible immediately at 1 % ──
    writeSession({ requestId: null, serviceName, progress: 1, checkedOnce: false  });
    advance(1);

    // ── Phase 1: get upload URL ──
    // Fast: 1 % → 25 % in 3 s.  Slow drip: up to 44 % (3 s / tick).
    simulate(25, 3000, 44);

    const prepareUrl = new URL("new_generate-resumable-url", endpointBase).toString();
    const prepareRes = await axios.post(
      prepareUrl,
      {
        patientId,
        serviceName,
        files: [{ fileNumber: 1, fileType: resolvedFileType, contentType: ct }],
      },
      { headers: { "Content-Type": "application/json" } }
    );

    stopTimer();

    const data = prepareRes.data;
    const rid  =
      data?.request_id || data?.requestId ||
      data?.requestid  || data?.requestID || null;
    const urls = data?.upload_urls || [];

    updateSession({ requestId: rid });
    advance(45); // real milestone

    // ── Phase 2: upload bytes to S3 ──
    // Real onUploadProgress maps 0-100 % of bytes to 45-88 % overall.
    advance(46); // ensure immediate small move
    await axios.put(urls[0]?.url, file, {
      headers:  { "Content-Type": ct },
      timeout:  120000,
      onUploadProgress: (evt) => {
        if (evt?.loaded && evt?.total > 0) {
          advance(45 + Math.round((evt.loaded / evt.total) * 43)); // 45→88
        }
      },
    });
    advance(89);

    // ── Phase 3: verify upload ──
    // Fast: 90 % → 95 % in 2 s.  Slow drip: up to 98 % (3 s / tick).
    simulate(95, 2000, 98);

    const checkUrl = new URL("check-uploaded-files", endpointBase).toString();
    const checkRes = await axios.post(
      checkUrl,
      {
        requestId:      rid,
        serviceName,
        sourceChannel: sourcechannel,  
        createdBy: createdBy,    
        signedUrlCount: 1,
        fileType:       resolvedFileType,
      },
      { headers: { "Content-Type": "application/json", accept: "application/json" } }
    );

    stopTimer();

    if (checkRes.data) {
      // check-uploaded-files only confirms the photo made it to storage — that's
      // "upload done". The AI report itself is generated async afterwards; that
      // wait is surfaced on the AI Reports page (Report Pending → View Report),
      // not here. Don't block this banner on it.
      advance(100);
      onSuccess?.();
    } else {
      throw new Error("Verification failed");
    }
  } catch (err) {
    stopTimer();
    console.error("[GlobalUpload] failed:", err);
    localStorage.removeItem("quantumUploadSession");
    onError?.(err);
  }
};
